import * as UI from "@iyk/ui"
import * as React from "react"

import { PlayPauseButton } from "./play-pause-button.tsx"
import { useAudioPlayer } from "./use-audio-player.ts"

export function AudioPlayer() {
  const { currentTrack } = useAudioPlayer()

  if (!currentTrack) return null

  return (
    <div>
      <SeekBar trackDuration={currentTrack.duration} />
      <div className="flex items-center gap-3 p-3">
        <div className="flex items-center gap-2 flex-1 overflow-hidden">
          <img
            src={currentTrack.playlist.coverImageUrl}
            alt={currentTrack.title}
            className="size-11 rounded-sm object-cover overflow-hidden shrink-0"
          />
          <div className="overflow-hidden flex-1">
            <p className="text-sm truncate text-ellipsis">{currentTrack.title}</p>
            <p className="text-xs text-gray-11 truncate text-ellipsis">{currentTrack.artist}</p>
          </div>
        </div>
        <div className="shrink-0">
          <Controls />
        </div>
      </div>
    </div>
  )
}

function Controls() {
  const { isPlaying, togglePlayPause, playPreviousTrack, playNextTrack } = useAudioPlayer()

  return (
    <div className="flex items-center justify-center gap-1.5">
      {/* Previous */}
      <button
        className="flex items-center justify-center size-5 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => playPreviousTrack()}
      >
        <UI.Icon.SkipBack className="size-4 text-gray-12" />
      </button>

      {/* Play / Pause */}
      <PlayPauseButton isPlaying={isPlaying} onClick={() => togglePlayPause()} />

      {/* Next */}
      <button
        className="flex items-center justify-center size-5 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => playNextTrack()}
      >
        <UI.Icon.SkipForward className="size-4 text-gray-12" />
      </button>
    </div>
  )
}

function SeekBar({ trackDuration }: { trackDuration: number }) {
  const { currentTrackTime, setCurrentTrackTime } = useAudioPlayer()
  const [localProgress, setLocalProgress] = React.useState(0)
  const [isSeeking, setIsSeeking] = React.useState(false)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalProgress(Number(event.target.value))
  }

  const handleSeekStart = () => {
    setIsSeeking(true)
  }

  const handleSeekEnd = () => {
    const newTime = (localProgress / 100) * trackDuration
    setCurrentTrackTime(newTime)
    setIsSeeking(false)
  }

  const progress = (currentTrackTime / trackDuration) * 100
  const progressToDisplay = isSeeking ? localProgress : progress

  return (
    <div className="relative w-full h-0.5">
      <div className="relative size-full bg-gray-6 overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-gray-12"
          style={{ width: `${progressToDisplay}%` }}
        />
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={localProgress}
        onChange={handleChange}
        onMouseDown={handleSeekStart}
        onMouseUp={handleSeekEnd}
        onTouchStart={handleSeekStart}
        onTouchEnd={handleSeekEnd}
        className={classNameForRangeInput()}
      />
    </div>
  )
}

const classNameForRangeInput = UI.cva([
  "size-full appearance-none absolute inset-0 opacity-0 cursor-pointer",
  "[&::-webkit-slider-thumb]:appearance-none",
  "[&::-webkit-slider-thumb]:size-3",
  "[&::-webkit-slider-thumb]:rounded-full",
  "[&::-webkit-slider-thumb]:bg-gray-12",
  "[&::-webkit-slider-thumb]:cursor-pointer",
  "[&::-moz-range-thumb]:appearance-none",
  "[&::-moz-range-thumb]:size-3",
])
